// src/components/IntakeForm.jsx
import { useState, useEffect, useMemo } from 'react';
import { useOrder } from '../contexts/OrderContext';
import { useUser } from '../contexts/UserContext';
import Spinner from './Spinner';
import { formatDispenseQuantity } from '../utilities/productDisplay';

function IntakeForm({ onSubmit, onCancel, isRefill = false }) {
    const [dosageOptions, setDosageOptions] = useState({}); // Structure: { drugName: [{ product_code: '101', dosage: '0.5 mg' }, ...] }
    const [pricing, setPricing] = useState({}); // Structure: { drugName: { product_code: price } }
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [formData, setFormData] = useState({});

    const { user } = useUser();
    const orderContext = useOrder();
    const hasMedicationSelection = Boolean(formData.drug);
    const requiresMedicationSelection = isRefill;
    const canSubmit = !isLoading
        && (!requiresMedicationSelection || (formData.product_code && formData.order_qty))
        && (!hasMedicationSelection || (formData.product_code && formData.order_qty));
        
    // --- Prefill Medical History ---
    const medicalHistoryPrefill = useMemo(() => ({
        Allergies: user?.allergies || '',
        Medications: user?.medications || '',
        Hospitalizations: user?.hospitalizations || '',
        Personal_History: user?.personal_illness || '',
        Family_History: user?.family_illness || '',
    }), [user]); // Recalculate only if user object changes

    const medicalHistoryString = useMemo(() => Object.entries(medicalHistoryPrefill)
        .filter(([, value]) => value) // Filter out entries with falsy values
        .map(([key, value]) => `${key.replace('_', ' ')}: ${value}`) // Format each entry
        .join('\n'), [medicalHistoryPrefill]);

    // --- Fetch Pricing and Dosage Info ---
    useEffect(() => {
			setError(null);
				// don't load until the data is available
				if(orderContext?.isLoading) return;

        // Basic user check
        if (!user?.userid) {
            setError('User session not found. Please log in.');
            setIsLoading(false);
            // Optional: navigate('/login');
            return;
        }

				try {
					const newDosageOptions = {};
					const newPricing = {};

					// Determine default height/weight based on gender
					const getDefaultHeight = () => {
						if (user?.height) return user.height;
						// Default heights in inches: Female=62", Male=69", Unknown=66"
						if (user?.gender?.toLowerCase() === 'female') return '62';
						if (user?.gender?.toLowerCase() === 'male') return '69';
						return '66'; // Unknown/not specified
					};

					const getDefaultWeight = () => {
						if (user?.weight) return user.weight;
						if (user?.orderHistory?.[0]?.weight) return user.orderHistory[0].weight;
						// Default weights in lbs: Female=165, Male=200, Unknown=180
						if (user?.gender?.toLowerCase() === 'female') return '165';
						if (user?.gender?.toLowerCase() === 'male') return '200';
						return '180'; // Unknown/not specified
					};

					const getDefaultGoalWeight = () => {
						if (user?.goal_weight) return user.goal_weight;
						// Default to 20 lbs less than current/default weight
						const currentWeight = parseFloat(getDefaultWeight());
						return !isNaN(currentWeight) ? String(Math.max(100, currentWeight - 20)) : '';
					};

					const latestDrug = orderContext?.latestOrder?.product_name?.toLowerCase() || '';
					const initialDrug = isRefill ? latestDrug : latestDrug;

					const initialFormData = {
						drug: initialDrug,
						product_code: orderContext?.latestOrder?.product_code || '',
						dosage: orderContext?.latestOrder?.dosage || '',
						currentMedication: isRefill ? 'yes' : (orderContext?.latestOrder?.product_name ? 'yes' : 'no'), // Force 'yes' for refill
						height: getDefaultHeight(),
						weight: getDefaultWeight(),
						desiredWeight: getDefaultGoalWeight(),
						order_qty: orderContext?.latestOrder?.order_qty || (isRefill ? '1' : ''),
						gastrointestinal: "no",
						abdominal_pain: "no"
					};

						setFormData(initialFormData);
        
					// load the raw data into the form fields
					orderContext?.pricingData.forEach(item => {
						const drugNameLower = item.product_name?.toLowerCase();
						const product_code = item.product_code;
						// Use the dosage field directly from API response
						const dosageValue = item.dosage || null;
						const price = parseFloat(item.price); // Price per months

						if (drugNameLower && product_code && dosageValue !== null && !isNaN(price)) {
							// Populate dosageOptions
							if (!newDosageOptions[drugNameLower]) {
									newDosageOptions[drugNameLower] = [];
							}
							// Avoid adding duplicates based on product_code
							if (!newDosageOptions[drugNameLower].some(d => d.product_code === product_code)) {
									newDosageOptions[drugNameLower].push({
										product_code: product_code,
										dosage: dosageValue,
										route: item.route,
										directions: item.directions,
										dispense_unit: item.dispense_unit,
										dispense_qty_per_month: item.dispense_qty_per_month,
									});
							}

							// Populate pricing
							if (!newPricing[drugNameLower]) {
									newPricing[drugNameLower] = {};
							}
							newPricing[drugNameLower][product_code] = price; // Store price per month
                        }
                    });

					// Sort dosages numerically by product_code within each drug
					Object.keys(newDosageOptions).forEach(drugKey => {
							newDosageOptions[drugKey].sort((a, b) => {
									const codeA = parseInt(a.product_code, 10);
									const codeB = parseInt(b.product_code, 10);
									return codeA - codeB;
							});
					});

					setDosageOptions(newDosageOptions);
					setPricing(newPricing);

					// --- Auto-select initial dosage display name after fetch ---
					// Find the dosage display name matching the initial product_code
					const initialDrugOptions = newDosageOptions[initialFormData.drug];
					const initialDoseInfo = initialDrugOptions?.find(d => d.product_code === initialFormData.product_code);
					if (initialDoseInfo && !initialFormData.dosage) { // Only set if not already set by lastOrder
						setFormData(prev => ({ ...prev, dosage: initialDoseInfo.dosage }));
					}
					// --- End auto-select ---

				} catch {
					setError('Error fetching medication information. Please try again later.');
				setDosageOptions({});
				setPricing({});
			} finally {
				setIsLoading(false);
			}

    }, [orderContext?.pricingData, orderContext?.isLoading]);

    // --- Calculate Allowed Product Codes for Refill ---
    const allowedProductCodes = useMemo(() => {
        if (!isRefill || !orderContext?.latestOrder?.product_code || isLoading || !dosageOptions[formData.drug]) {
            return null;
        }
        const lastCodeStr = orderContext.latestOrder.product_code;
        const currentDrugDosages = dosageOptions[formData.drug];
        const currentIndex = currentDrugDosages.findIndex(d => d.product_code === lastCodeStr);

        if (currentIndex === -1) {
            return new Set([lastCodeStr]);
        }
        // currentDrugDosages is already scoped to formData.drug (== product_name),
        // so any neighboring dose in this array is the same product family by
        // construction — no product-code arithmetic needed to confirm that.
        const allowedCodes = new Set([lastCodeStr]);
        if (currentIndex > 0) {
            const prevDose = currentDrugDosages[currentIndex - 1];
            allowedCodes.add(prevDose.product_code);
        }
        if (currentIndex < currentDrugDosages.length - 1) {
            const nextDose = currentDrugDosages[currentIndex + 1];
            allowedCodes.add(nextDose.product_code);
        }
        return allowedCodes;
    }, [isRefill, orderContext?.latestOrder?.product_code, isLoading, dosageOptions, formData.drug]);

    // --- Calculate Total Price ---
    const calculatedPrice = useMemo(() => {
        const { drug, product_code, order_qty } = formData;
        const quantity = parseInt(order_qty, 10);

        // Ensure all necessary data is available
        if (!drug || !product_code || !quantity || !pricing[drug] || pricing[drug][product_code] === undefined) {
            return null; // Not enough info to calculate
        }

        const basePrice = pricing[drug][product_code]; // Price per month
        const totalPrice = basePrice * quantity;

        return !isNaN(totalPrice) ? totalPrice : null;

    }, [formData.drug, formData.product_code, formData.order_qty, pricing]);

    // --- Dispensed quantity for the selected dose + duration (e.g. "90 Tablets") ---
    const selectedDoseInfo = useMemo(() => {
        return dosageOptions[formData.drug]?.find(d => d.product_code === formData.product_code) || null;
    }, [dosageOptions, formData.drug, formData.product_code]);
    const dispensedQuantity = useMemo(() => {
        return formatDispenseQuantity(selectedDoseInfo, formData.order_qty);
    }, [selectedDoseInfo, formData.order_qty]);


    // --- Height Options ---
    const heightOptions = useMemo(() => {
        const options = [];
        const minInches = 4 * 12;
        const maxInches = 7 * 12 + 6;
        for (let inches = minInches; inches <= maxInches; inches++) {
            const feet = Math.floor(inches / 12);
            const remainingInches = inches % 12;
            const display = `${feet}' ${remainingInches}"`;
            options.push({ value: inches.toString(), display });
        }
        return options;
    }, []);

    // --- Handle Form Changes ---
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newState = { ...prev, [name]: value };
            if (name === 'drug') {
                if (!value) {
                    newState.product_code = '';
                    newState.dosage = '';
                    newState.order_qty = '';
                    newState.currentMedication = 'no';
                    return newState;
                }
                const newDrugOptions = dosageOptions[value];
                newState.product_code = newDrugOptions?.[0]?.product_code || '';
                newState.dosage = newDrugOptions?.[0]?.dosage || '';
                newState.order_qty = newState.order_qty || '1';
            }
            if (name === 'product_code') {
                const selectedDrugOptions = dosageOptions[newState.drug];
                const selectedDoseInfo = selectedDrugOptions?.find(d => d.product_code === value);
                newState.dosage = selectedDoseInfo?.dosage || '';
            }
            if (name === 'currentMedication' && value === 'no' && !isRefill) {
                 const currentDrugOptions = dosageOptions[newState.drug];
                 newState.product_code = currentDrugOptions?.[0]?.product_code || '';
                 newState.dosage = currentDrugOptions?.[0]?.dosage || '';
            }
            return newState;
        });
    };

    // --- Submit Handler ---
    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      setMessage('');
      if (!user.userid) {
          setError('User information is missing. Please log in again.');
          return;
      }
      if ((requiresMedicationSelection || hasMedicationSelection) && !formData.product_code) {
           setError('Please select a dosage.');
           return;
      }
      if ((requiresMedicationSelection || hasMedicationSelection) && !formData.order_qty) {
           setError('Please select the number of months.');
           return;
      }
      if (!formData.complaint) { // Ensure complaint/notes are provided
          setError('Please provide notes for the provider.');
          return;
      }

	      // Pass the raw formData back to the parent component's onSubmit handler
	      onSubmit(formData);
	  };

    // --- Styling Classes ---
    const inputClassName = "w-full p-2 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary";
    const labelClassName = "block text-primary mb-1";

    // --- Render Logic ---
    if (isLoading && Object.keys(dosageOptions).length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner>
                	<span className="ml-2">Loading medication options...</span>
								</Spinner> 
            </div>
        );
    }

    return (
        <div id="intake-form-component" className="flex flex-col justify-start flex-grow w-full">
            <h2 className="text-lg font-bold text-primary mb-0">Patient Information</h2>
            <p className="font-bold text-secondary-dark mb-4">Please review and update your information carefully before submitting.</p>

            {error && !message && Object.keys(dosageOptions).length === 0 && (
                <p className="text-red-500 text-sm mb-4">{error}</p>
            )}

            <form onSubmit={handleSubmit} className="flex flex-grow flex-col w-full space-y-4">
                {/* --- Drug, Dosage, Qty, Price Row --- */}
                <div className="flex flex-col md:flex-row items-start md:items-end justify-start mb-4 w-full gap-4 md:gap-6">
                    {/* Drug Selection */}
                    <div className="flex flex-col w-full md:w-auto">
                        <label htmlFor="drug" className={labelClassName}>
                            {isRefill ? 'Medication' : 'Medication Preference'}
                        </label>
                        {!isRefill && (
                            <p className="text-xs text-gray-500 mb-1 max-w-sm">
                                Optional for your first appointment. Your provider will review your history and recommend the best treatment plan.
                            </p>
                        )}
                        <select
                            id="drug" name="drug" value={formData.drug} onChange={handleFormChange}
                            className={`${isRefill ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'} ${inputClassName} min-w-[180px]`}
                            required={isRefill} disabled={isLoading || isRefill}
                        >
                            {!isRefill && (
                                <option value="">Not sure - discuss with provider</option>
                            )}
                            {Object.keys(dosageOptions).length === 0 && !isLoading && (
                                <option value="" disabled>No options available</option>
                            )}
                            {Object.keys(dosageOptions).map((drugKey) => (
                                <option key={drugKey} value={drugKey}>
                                    {drugKey.charAt(0).toUpperCase() + drugKey.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Current Medication Question (Hidden for Refills) */}
                    {!isRefill && formData.drug && (
                        <div className="flex flex-col w-full md:w-auto">
                            <label htmlFor="currentMedication" className={labelClassName}>Are you currently taking this medication?</label>
                            <select
                                id="currentMedication" name="currentMedication" value={formData.currentMedication} onChange={handleFormChange}
                                className={`${inputClassName} bg-white min-w-[100px]`}
                                required disabled={isLoading}
                            >
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                            </select>
                        </div>
                    )}

                    {/* Dosage Selection */}
                    {formData.drug && dosageOptions[formData.drug] && (
                        <div className="flex flex-col w-full md:w-auto">
                            <label htmlFor="product_code" className={labelClassName}>
                                {isRefill ? 'Select Refill Dosage' : (formData.currentMedication === 'yes' ? 'Current Dose' : 'Preferred Starting Dose')}
                            </label>
                            <select
                                id="product_code" name="product_code" value={formData.product_code} onChange={handleFormChange}
                                className={`${inputClassName} bg-white min-w-[150px]`}
                                required disabled={isLoading}
                            >
                                <option value="">Select Dosage</option>
                                {dosageOptions[formData.drug]?.map((dose) => {
                                    const isDisabledForRefill = isRefill && allowedProductCodes && !allowedProductCodes.has(dose.product_code);
                                    return (
                                        <option
                                            key={dose.product_code} value={dose.product_code}
                                            disabled={isDisabledForRefill}
                                            className={isDisabledForRefill ? 'text-gray-400 bg-gray-100' : ''}
                                        >
                                            {dose.dosage}
                                        </option>
                                    );
                                })}
                            </select>
                            {selectedDoseInfo?.route && (
                                <p className="mt-1 text-xs text-neutral-dark">{selectedDoseInfo.route}{selectedDoseInfo.directions ? ` — ${selectedDoseInfo.directions}` : ''}</p>
                            )}
                        </div>
                    )}

                    {/* Qty Selection */}
                    {formData.drug && dosageOptions[formData.drug] && (
                        <div className="flex flex-col w-full md:w-auto">
                            <label htmlFor="order_qty" className={labelClassName}>
                                {isRefill ? 'Select Duration' : 'Preferred Duration'}
                            </label>
                            <select
                                id="order_qty" name="order_qty" value={formData.order_qty} onChange={handleFormChange}
                                className={`${inputClassName} bg-white min-w-[150px]`}
                                required disabled={isLoading}
                            >
                                <option value="">Select Months</option>
                                <option value="1">1 Month</option>
                                <option value="3">3 Months</option>
                                <option value="6">6 Months</option>
                            </select>
                        </div>
                    )}

                    {!isRefill && !formData.drug && (
                        <div className="w-full md:flex-1 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                            No medication selected. Your provider will discuss options during the appointment.
                        </div>
                    )}

                    {/* --- Calculated Price Display --- */}
                    {calculatedPrice !== null && calculatedPrice > 0 && (
                        <div className="flex flex-col w-full md:w-auto md:ml-auto"> {/* Aligns to the right on medium+ screens */}
                            <label className={labelClassName}>Estimated Price</label>
                            <div className="p-2 border border-neutral rounded bg-gray-100 text-right min-w-[120px]">
                                <span className="font-semibold text-lg text-secondary-dark">
                                    ${calculatedPrice.toFixed(2)}
                                </span>
                                {dispensedQuantity && (
                                    <span className="block text-xs text-neutral-dark">{dispensedQuantity}</span>
                                )}
                            </div>
                        </div>
                    )}
                    {/* --- End Calculated Price Display --- */}
                </div>

                {/* --- Height, Weight, Goal Weight Row --- */}
                <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-4 w-full gap-4 md:gap-6">
									<div className="flex flex-col w-full md:w-1/3">
											<label htmlFor="height" className={labelClassName}>Height *</label>
											<select
													id="height" name="height" value={formData.height} onChange={handleFormChange}
													className={`${inputClassName} bg-white`}
													required disabled={isLoading}
											>
													<option value="">Select Height</option>
													{heightOptions.map(option => (
															<option key={option.value} value={option.value}>{option.display}</option>
													))}
											</select>
									</div>
									<div className="flex flex-col w-full md:w-1/3">
											<label htmlFor="weight" className={labelClassName}>Current Weight (lbs) *</label>
											<input
													type="number" name="weight" step="0.1" placeholder="e.g., 185.5"
													value={formData.weight} onChange={handleFormChange}
													className={`${inputClassName} text-right`}
													required disabled={isLoading}
											/>
									</div>
									<div className="flex flex-col w-full md:w-1/3">
											<label htmlFor="desiredWeight" className={labelClassName}>Goal Weight (lbs) *</label>
											<input
													type="number" name="desiredWeight" step="1" placeholder="e.g., 150"
													value={formData.desiredWeight} onChange={handleFormChange}
													className={`${inputClassName} text-right`}
													required disabled={isLoading}
											/>
									</div>
                </div>

								<div>
									<label htmlFor="complaint" className={labelClassName}>
											Medical History & Notes for Provider *
									</label>
									{/* Display weightloss specific symptoms */}
									{ isRefill && (
										<div className="w-full md:w-1/2 mx-auto mb-4">
											<div className="flex w-full items-center justify-start mt-4">
												<input
														type="checkbox"
														id="abdominal_pain"
														checked={formData.abdominal_pain === "yes" || formData.abdominal_pain === "abdominal_pain"}
														onChange={(e) => {
															setFormData(prev => ({
																...prev,
																abdominal_pain: e.target.checked ? "yes" : "no"
															}))
														}}
														name="abdominal_pain"
														className="h-6 w-6 flex-grow-0 flex-shrink-0 self-start border-2 rounded border-neutral-dark text-primary focus:ring-2 focus:ring-primary checked:bg-primary"
												/>
												<label htmlFor="abdominal_pain" className="ml-3 block text-sm text-neutral-dark">
														Have you experienced any moderate to severe abdominal pain?
												</label>
											</div>

											<div className="flex w-full items-center justify-start mt-4">
												<input
														type="checkbox"
														id="gastrointestinal"
														name="gastrointestinal"
														checked={formData.gastrointestinal === "yes" || formData.gastrointestinal === "gastrointestinal"}
														onChange={(e) => {
															setFormData(prev => ({
																...prev,
																gastrointestinal: e.target.checked ? "yes" : "no"
															}))
														}}
														className="h-6 w-6 flex-grow-0 flex-shrink-0 self-start border-2 rounded border-neutral-dark text-primary focus:ring-2 focus:ring-primary checked:bg-primary"
												/>
												<label htmlFor="gastrointestinal" className="ml-3 block text-sm text-neutral-dark">
														Have you experienced moderate to severe nausea, vomiting or diarrhea?
												</label>
											</div>
										</div>
									)}

									{/* Display prefilled history */}
									{medicalHistoryString && (
											<p className="text-sm text-gray-500 mb-1">
													<span className="font-semibold">Previously Shared:</span> {medicalHistoryString.replace(/\n/g, '; ')} {/* Display inline */}
											</p>
									)}

									{/* --- Medical History / Complaint Textarea --- */}
									<p className="text-sm text-gray-500 mb-2">
											Please add any other symptoms or notes for your provider below. (If none, enter "None").
									</p>
									<textarea
											id="complaint" name="complaint"
											placeholder="Example: Feeling good, no side effects. OR: Experiencing mild nausea..."
											value={formData.complaint} onChange={handleFormChange}
											className={inputClassName} rows="4" // Reduced rows slightly
											disabled={isLoading}
									/>
							</div>

                {/* --- Footer: Notes, Messages, Submit --- */}
                <div className="flex flex-col flex-grow w-full justify-end items-center mt-auto pt-4">
                    <p className="text-gray-500 text-sm mt-1 mb-2 text-center">
                        Note: Your provider will review your intake form and determine whether medication is appropriate. If approved, medication and dosage will be finalized after the visit.
                    </p>
                    {message && <p className="text-green-500 text-sm mb-2">{message}</p>}
                    {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
										<div className="w-full flex flex-row justify-end items-center gap-2">
											{onCancel && (
													<button
															type="button" onClick={onCancel}
															className={`w-1/2 md:w-1/3 h-max p-3 mb-2 bg-gray-300  text-gray-600   hover:bg-button-light hover:text-white transition duration-200`}
															disabled={isLoading}
													>
															Cancel
													</button>
											)}
											<button
													type="submit"
													className={`w-1/2 md:w-1/3 h-max p-3 mb-2 bg-button text-white  rounded hover:bg-button-light transition duration-200 ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
													disabled={!canSubmit}
											>
													{isLoading ? 'Processing...' : 'Save and Continue'}
											</button>
									</div>
                </div>
            </form>
        </div>
    );
}

export default IntakeForm;
